import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Input from '../../utils/Input';
import Button from '../../utils/Button';

export interface CanvasserFormData {
  fullName: string;
  email: string;
  // role: 'CANVASSER' | 'PARTNER_ADMIN';
}

interface AddCanvasserFormProps {
  onSubmit: (data: CanvasserFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  title?: string;
}

const AddCanvasserForm: React.FC<AddCanvasserFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
  title = "Add New Canvasser"
}) => {
  const [formData, setFormData] = useState<CanvasserFormData>({
    fullName: '',
    email: '',
    // role: 'CANVASSER'
  });

  const [errors, setErrors] = useState<Partial<CanvasserFormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<CanvasserFormData> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit({
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        // role: formData.role
      });
    }
  };

  const handleInputChange = (field: keyof CanvasserFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-200"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onCancel}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Full Name"
            placeholder="Enter canvasser's full name"
            value={formData.fullName}
            onChange={(e) => handleInputChange('fullName', e.target.value)}
            error={errors.fullName}
            disabled={isSubmitting}
            size="medium"
            color="white"
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="Enter email address"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={errors.email}
            disabled={isSubmitting}
            size="medium"
            color="white"
          />



          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              variant="secondary"
              label="Cancel"
            >

            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              label={isSubmitting ? "Inviting..." : "Send Invitation"}
            >
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AddCanvasserForm;
